"use client"

import { useState, useEffect } from "react"
import { Check, X, MessageCircle, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"

const plans = [
  {
    name: "Plano Start",
    badge: "Mais Popular",
    setup: "450",
    monthly: "60",
    features: [
      { text: "Plataforma completa com site e painel administrativo" },
      { text: "Home page personalizavel" },
      { text: "Catalogo de produtos ou servicos" },
      { text: "Blog" },
      { text: "Agenda para eventos, datas e promocoes" },
    ],
    note: "*Personalizacoes densas sob consulta",
    gradient: "from-purple-600 via-violet-500 to-cyan-400",
    borderColor: "border-cyan-400/40",
    glowColor: "shadow-cyan-500/20",
  },
  {
    name: "Plano Pro",
    badge: null,
    setup: "650",
    monthly: "95",
    features: [
      { text: "Catalogo completo de produtos e servicos" },
      { text: "Melhor desempenho e agenda avancada" },
      { text: "Mais possibilidades de personalizacao" },
      { text: "Seguranca e Dados Avancada" },
      { text: "Recuperacao assistida pelo suporte" },
    ],
    note: null,
    gradient: "from-purple-700 via-violet-600 to-purple-500",
    borderColor: "border-purple-400/40",
    glowColor: "shadow-purple-500/20",
  },
  {
    name: "Plano Premium",
    badge: null,
    setup: "850",
    monthly: "130",
    features: [
      { text: "Personalizacoes visuais inclusas" },
      { text: "Estrutura preparada para crescimento" },
      { text: "Atendimento prioritario e monitoramento continuo" },
      { text: "Maxima Estabilidade e Seguranca" },
      { text: "Protecao de dados avancada" },
      { text: "Recuperacao assistida com prioridade" },
    ],
    note: "*Personalizacoes densas sob consulta",
    gradient: "from-blue-700 via-purple-600 to-violet-500",
    borderColor: "border-violet-400/40",
    glowColor: "shadow-violet-500/20",
  },
]

function PricingCard({
  plan,
  compact = false,
  inModal = false,
}: { plan: (typeof plans)[0]; compact?: boolean; inModal?: boolean }) {
  const phoneNumber = "5551983204177"
  const isCompact = compact || inModal

  return (
    <div
      className={`relative flex flex-col rounded-2xl overflow-hidden ${plan.borderColor} border ${plan.glowColor} shadow-xl group hover:-translate-y-1 transition-all duration-300`}
    >
      <div className={`absolute inset-0 bg-gradient-to-b ${plan.gradient} opacity-20 pointer-events-none`} />
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm pointer-events-none" />

      {plan.badge && (
        <div className="absolute top-0 right-0 z-10">
          <div className="bg-cyan-400 text-slate-900 text-xs font-bold px-3 py-1.5 rounded-bl-lg uppercase tracking-wide">
            {plan.badge}
          </div>
        </div>
      )}

      <div
        className={`relative z-10 flex flex-col flex-1 ${inModal ? "p-3 lg:p-5" : compact ? "p-4" : "p-6 lg:p-8"}`}
      >
        <h3
          className={`font-bold text-white ${inModal ? "text-base lg:text-lg mb-2" : compact ? "text-lg mb-4" : "text-xl lg:text-2xl mb-4"} bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}
        >
          {plan.name}
        </h3>

        <div className={`flex items-baseline gap-2 flex-wrap ${inModal ? "mb-1" : isCompact ? "mb-4" : "mb-6"}`}>
          <div className="flex items-baseline">
            <span className="text-slate-400 text-xs">{"R$"}</span>
            <span
              className={`font-bold text-white ${inModal ? "text-2xl lg:text-3xl" : compact ? "text-3xl" : "text-4xl lg:text-5xl"}`}
            >
              {plan.setup}
            </span>
          </div>
          <div className={`w-px bg-slate-600 ${inModal ? "h-6" : "h-8"}`} />
          <div className="flex items-baseline">
            <span className="text-slate-400 text-xs">{"R$"}</span>
            <span
              className={`font-bold text-white ${inModal ? "text-lg lg:text-xl" : compact ? "text-xl" : "text-2xl lg:text-3xl"}`}
            >
              {plan.monthly}
            </span>
            <span className="text-slate-400 text-xs">/mes</span>
          </div>
        </div>

        <p className={`text-slate-500 ${inModal ? "text-[10px] mb-2" : "text-xs mb-4"}`}>setup inicial</p>

        <ul className={`${inModal ? "space-y-1.5 mb-2" : isCompact ? "space-y-3 mb-4" : "space-y-3 mb-6"}`}>
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-1.5">
              <Check
                className={`text-cyan-400 flex-shrink-0 ${inModal ? "w-3 h-3 mt-0.5" : "w-4 h-4 mt-0.5"}`}
                aria-hidden="true"
              />
              <span
                className={`text-slate-300 leading-snug ${inModal ? "text-[11px] lg:text-xs" : isCompact ? "text-xs" : "text-sm"}`}
              >
                {feature.text}
              </span>
            </li>
          ))}
        </ul>

        {plan.note && (
          <p className={`text-slate-500 italic ${inModal ? "text-[9px] mb-1" : isCompact ? "text-[10px] mb-3" : "text-xs mb-4"}`}>
            {plan.note}
          </p>
        )}

        <div className={`mt-auto ${inModal ? "pt-2" : "pt-4"}`}>
          <Button
            asChild
            className={`w-full bg-gradient-to-r ${plan.gradient} hover:opacity-90 text-white font-semibold border-0 ${inModal ? "text-xs h-8" : isCompact ? "text-xs h-9" : ""}`}
          >
            <a
              href={`https://wa.me/${phoneNumber}?text=Olá! Tenho interesse no ${plan.name} da Void Agência.`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 justify-center"
              aria-label={`Contratar ${plan.name} via WhatsApp`}
            >
              <MessageCircle size={inModal ? 12 : isCompact ? 14 : 16} aria-hidden="true" />
              Contratar
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

function MobileAccordionCard({ 
  plan, 
  isOpen, 
  onToggle 
}: { 
  plan: (typeof plans)[0]
  isOpen: boolean
  onToggle: () => void
}) {
  const phoneNumber = "5551983204177"

  return (
    <div className={`relative rounded-xl overflow-hidden ${plan.borderColor} border ${plan.glowColor} shadow-lg`}>
      <div className={`absolute inset-0 bg-gradient-to-b ${plan.gradient} opacity-20`} />
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />

      <div className="relative z-10">
        {plan.badge && (
          <div className="absolute top-0 right-0 z-10">
            <div className="bg-cyan-400 text-slate-900 text-[10px] font-bold px-2 py-1 rounded-bl-lg uppercase tracking-wide">
              {plan.badge}
            </div>
          </div>
        )}

        {/* Always visible: name + price + toggle */}
        <button
          onClick={onToggle}
          className="w-full text-left p-4 flex items-center justify-between gap-3"
          aria-expanded={isOpen}
          aria-label={`${isOpen ? "Fechar" : "Ver"} detalhes do ${plan.name}`}
        >
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold text-base bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent mb-1`}>
              {plan.name}
            </h3>
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="flex items-baseline">
                <span className="text-slate-400 text-[10px]">{"R$"}</span>
                <span className="font-bold text-white text-2xl">{plan.setup}</span>
              </div>
              <div className="w-px h-5 bg-slate-600" />
              <div className="flex items-baseline">
                <span className="text-slate-400 text-[10px]">{"R$"}</span>
                <span className="font-bold text-white text-lg">{plan.monthly}</span>
                <span className="text-slate-400 text-[10px]">/mes</span>
              </div>
              <span className="text-slate-500 text-[10px]">setup inicial</span>
            </div>
          </div>
          <ChevronDown
            size={18}
            className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        {/* Expandable: features + CTA */}
        <div
          className={`overflow-hidden transition-all duration-200 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
        >
          <div className="px-4 pb-4">
            <ul className="space-y-2 mb-3">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <Check className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-slate-300 text-xs leading-snug">{feature.text}</span>
                </li>
              ))}
            </ul>

            {plan.note && <p className="text-slate-500 italic text-[10px] mb-3">{plan.note}</p>}

            <Button
              asChild
              className={`w-full bg-gradient-to-r ${plan.gradient} hover:opacity-90 text-white font-semibold border-0 text-xs h-9`}
            >
              <a
                href={`https://wa.me/${phoneNumber}?text=Olá! Tenho interesse no ${plan.name} da Void Agência.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 justify-center"
                aria-label={`Contratar ${plan.name} via WhatsApp`}
              >
                <MessageCircle size={14} aria-hidden="true" />
                Contratar
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function PricingModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [openAccordion, setOpenAccordion] = useState<number | null>(null)

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Planos e Precos"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div className="relative w-full max-w-[80vw] max-h-[90vh] rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 p-4 lg:p-6 border-b border-slate-800">
          <h2 className="text-xl lg:text-2xl font-bold text-white">Nossos Planos</h2>
          <p className="text-slate-400 text-sm mt-1">Escolha o plano ideal para o seu negocio</p>
        </div>

        {/* Content - scrollable */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="p-4 lg:p-6 flex flex-col flex-1 overflow-y-auto min-h-0">
            {/* Desktop: 3 side by side */}
            <div className="hidden md:grid md:grid-cols-3 gap-4">
              {plans.map((plan, idx) => (
                <PricingCard key={idx} plan={plan} inModal />
              ))}
            </div>

            {/* Mobile: accordion cards - scrollable */}
            <div className="md:hidden flex flex-col gap-3">
              {plans.map((plan, idx) => (
                <MobileAccordionCard 
                  key={idx} 
                  plan={plan}
                  isOpen={openAccordion === idx}
                  onToggle={() => setOpenAccordion(openAccordion === idx ? null : idx)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer with close button */}
        <div className="flex-shrink-0 p-4 lg:p-6 border-t border-slate-800 text-center">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors text-sm font-medium"
            aria-label="Fechar modal de precos"
          >
            <X size={16} aria-hidden="true" />
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

export function PricingSection() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem("pricing-modal-shown")
    if (!alreadyShown) {
      const timer = setTimeout(() => {
        setIsModalOpen(true)
        sessionStorage.setItem("pricing-modal-shown", "true")
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [])

  return (
    <>
      <section id="pricing" className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Nossos Planos</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Planos acessiveis para cada etapa do seu negocio. Escolha o ideal e comece agora.
            </p>
          </div>

          {/* Desktop: 3 side by side */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {plans.map((plan, idx) => (
              <PricingCard key={idx} plan={plan} />
            ))}
          </div>

          {/* Mobile: stacked with compact cards */}
          <div className="md:hidden flex flex-col gap-6">
            {plans.map((plan, idx) => (
              <PricingCard key={idx} plan={plan} compact />
            ))}
          </div>
        </div>
      </section>

      <PricingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  )
}
